import { getDocs, query } from 'firebase/firestore';
import {
  auditedAddDoc as addDoc,
  auditedUpdateDoc as updateDoc,
  auditedDeleteDoc as deleteDoc,
} from './auditedWrites';
import { getEffectiveTenantId, tenantCollection, tenantDoc } from './tenantContext';

const COLLECTION = 'clientSites';

export async function getClientSites() {
  const tid = await getEffectiveTenantId();
  if (!tid) return [];
  const snap = await getDocs(query(tenantCollection(tid, COLLECTION)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

function buildClientSiteDoc(data, tenantId) {
  if (!tenantId || typeof tenantId !== 'string') {
    throw new Error('tenantId is required.');
  }
  const entries = Array.isArray(data.reimbursementMethodEntries) ? data.reimbursementMethodEntries : [];
  return {
    tenantId,
    clientId: (data.clientId || '').trim() || null,
    siteName: (data.siteName || '').trim() || null,
    siteStreet1: (data.siteStreet1 || '').trim() || null,
    siteStreet2: (data.siteStreet2 || '').trim() || null,
    siteCity: (data.siteCity || '').trim() || null,
    siteState: (data.siteState || '').trim() || null,
    siteZip: (data.siteZip || '').trim() || null,
    status: (data.status || 'active').toLowerCase() === 'inactive' ? 'inactive' : 'active',
    locality: (data.locality || '').trim() || null,
    siteType: (data.siteType || '').trim() || null,
    siteContactPerson: (data.siteContactPerson || '').trim() || null,
    siteContactEmail: (data.siteContactEmail || '').trim() || null,
    siteContactPhone: (data.siteContactPhone || '').trim() || null,
    reimbursementMethodEntries: entries.map((e) => ({
      reimbursementMethodId: e.reimbursementMethodId || null,
      effectiveDate: e.effectiveDate || null,
      effectiveThruDate: e.effectiveThruDate || null,
    })),
  };
}

export async function createClientSite(data) {
  const tid = await getEffectiveTenantId();
  if (!tid) throw new Error('Could not resolve tenant for this site.');
  const docData = buildClientSiteDoc(data, tid);
  const ref = await addDoc(tenantCollection(tid, COLLECTION), docData);
  return ref.id;
}

export async function updateClientSite(id, data) {
  const tenantId = await getEffectiveTenantId();
  if (!tenantId) throw new Error('Could not resolve tenant.');
  const docData = buildClientSiteDoc(data, tenantId);
  await updateDoc(tenantDoc(tenantId, COLLECTION, id), docData);
}

/** Bulk patch of site type / locality / methods only (other site fields untouched). */
export async function patchClientSiteBilling(id, { siteType, siteState, locality, reimbursementMethodEntries }) {
  const tenantId = await getEffectiveTenantId();
  if (!tenantId) throw new Error('Could not resolve tenant.');
  const patch = {};
  if (siteType !== undefined) patch.siteType = siteType || null;
  if (siteState !== undefined) patch.siteState = siteState || null;
  if (locality !== undefined) patch.locality = locality || null;
  if (reimbursementMethodEntries !== undefined) {
    patch.reimbursementMethodEntries = reimbursementMethodEntries.map((e) => ({
      reimbursementMethodId: e.reimbursementMethodId || null,
      effectiveDate: e.effectiveDate || null,
      effectiveThruDate: e.effectiveThruDate || null,
    }));
  }
  await updateDoc(tenantDoc(tenantId, COLLECTION, id), patch);
}

export async function deleteClientSite(id) {
  const tenantId = await getEffectiveTenantId();
  if (!tenantId) throw new Error('Could not resolve tenant.');
  await deleteDoc(tenantDoc(tenantId, COLLECTION, id));
}
